import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import {
  CreditCard, Banknote, Smartphone, Plus, Trash2, Minus,
  CheckCircle, Receipt, Users, Clock, Scissors, Printer, ShoppingBag,
  Search, Package, BarChart3, CalendarDays, LogOut, Lock, Eye, EyeOff,
  AlertTriangle, History, DoorOpen, DoorClosed, Coins, XCircle, ShoppingCart,
  UserPlus, FolderOpen, ArrowLeftRight, Edit3, X, Monitor, Shield, Percent
} from "lucide-react";
import InstallAppButton from "@/components/InstallAppButton";

// ------ Types ------
interface Service { id: string; name: string; price: number; duration_minutes: number; }
interface Product { id: string; name: string; price: number; stock: number; is_promotional: boolean; promotional_price: number; }
interface CartItem { id: string; name: string; price: number; quantity: number; staffId: string; type: "service" | "product"; originalId: string; }
interface LogEntry { id: string; user_email: string; user_name: string; action: string; details: any; created_at: string; }
interface SaleRecord { id: string; client_name: string; total_price: number; status: string; booking_date: string; booking_time: string; notes: string; services?: { name: string } | null; staff?: { name: string } | null; }

interface OpenAccount {
  id: string;
  clientName: string;
  clientPhone: string;
  clientEmail: string;
  items: CartItem[];
  notes: string;
  openedAt: string;
}

interface PdvSession {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string;
  status: string;
  opening_amount: number;
  opened_at: string;
  closed_at: string | null;
  session_token: string;
}

// ------ PDV Login Screen ------
const PDVLogin = ({ onLogin, sessionError }: { onLogin: (user: User) => void; sessionError?: string }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(sessionError || "");
  const [forceDisconnect, setForceDisconnect] = useState(false);
  const [forceEmail, setForceEmail] = useState("");
  const [forcePassword, setForcePassword] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    if (sessionError) setError(sessionError);
  }, [sessionError]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) onLogin(session.user);
    });
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!email || !password) { setError("Preencha email e senha"); return; }
    setLoading(true);
    const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (authError) { setError("Email ou senha incorretos"); return; }
    if (data.user) { toast({ title: "Sessão iniciada!" }); onLogin(data.user); }
  };

  const handleForceDisconnect = async () => {
    if (!forceEmail || !forcePassword) { setError("Preencha email e senha para desconectar"); return; }
    setLoading(true);
    // Sign in to verify credentials
    const { data, error: authError } = await supabase.auth.signInWithPassword({ email: forceEmail, password: forcePassword });
    if (authError || !data.user) {
      setLoading(false);
      setError("Credenciais incorretas");
      return;
    }
    // Close all open sessions for this user
    await supabase.from("pdv_sessions").update({
      status: "closed",
      closed_at: new Date().toISOString(),
      closed_by_name: "Desconexão forçada",
    }).eq("user_id", data.user.id).eq("status", "open");

    toast({ title: "Sessão anterior encerrada. Pode agora iniciar." });
    setForceDisconnect(false);
    setLoading(false);
    onLogin(data.user);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-sm w-full p-6 sm:p-8 space-y-6">
        <div className="text-center">
          <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
            <CreditCard size={28} className="text-primary" />
          </div>
          <h1 className="font-display text-2xl font-bold">Caixa (PDV)</h1>
          <p className="text-sm text-muted-foreground">Faça login para iniciar</p>
        </div>

        {forceDisconnect ? (
          <div className="space-y-4">
            <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3">
              <p className="text-sm text-amber-700 flex items-center gap-2"><AlertTriangle size={14} /> Este utilizador já está conectado noutro local. Insira as credenciais para desconectar a sessão anterior.</p>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={forceEmail} onChange={e => setForceEmail(e.target.value)} placeholder="seu@email.com" autoFocus />
            </div>
            <div className="space-y-2">
              <Label>Senha</Label>
              <Input type="password" value={forcePassword} onChange={e => setForcePassword(e.target.value)} placeholder="********" />
            </div>
            {error && <p className="text-destructive text-sm flex items-center gap-1"><AlertTriangle size={14} /> {error}</p>}
            <Button onClick={handleForceDisconnect} className="w-full rounded-full gap-2" disabled={loading}>
              <LogOut size={16} /> {loading ? "A desconectar..." : "Desconectar e Entrar"}
            </Button>
            <Button variant="ghost" onClick={() => { setForceDisconnect(false); setError(""); }} className="w-full">Voltar</Button>
          </div>
        ) : (
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="seu@email.com" autoFocus />
            </div>
            <div className="space-y-2">
              <Label>Senha</Label>
              <div className="flex gap-2">
                <Input type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="********" className="flex-1" />
                <Button type="button" variant="outline" size="icon" onClick={() => setShowPw(!showPw)}>
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </Button>
              </div>
            </div>
            {error && (
              <div className="space-y-2">
                <p className="text-destructive text-sm flex items-center gap-1"><AlertTriangle size={14} /> {error}</p>
                {error.includes("conectado") && (
                  <Button type="button" variant="outline" size="sm" onClick={() => { setForceDisconnect(true); setForceEmail(email); setForcePassword(password); setError(""); }} className="w-full text-xs gap-1">
                    <LogOut size={12} /> Desconectar sessão anterior
                  </Button>
                )}
              </div>
            )}
            <Button type="submit" className="w-full rounded-full gap-2" disabled={loading}>
              <Lock size={16} /> {loading ? "A entrar..." : "Entrar no PDV"}
            </Button>
          </form>
        )}
        <div className="pt-2 border-t border-border">
          <InstallAppButton label="Instalar PDV como aplicativo" variant="outline" fullWidth />
        </div>
      </Card>
    </div>
  );
};

// ------ Cart Panel ------
const CartPanel = ({
  cart, staffList, total, subtotal, discountType, setDiscountType, discountValue, setDiscountValue, discountAmount,
  paymentMethod, setPaymentMethod, amountReceived, setAmountReceived,
  changeAmount, clientName, setClientName, clientPhone, setClientPhone, notes, setNotes,
  updateQuantity, removeFromCart, updateCartStaff, finalizeSale, onSwapItem, onSaveAccount, activeAccountId, operatorName,
}: {
  cart: CartItem[]; staffList: { id: string; name: string }[]; total: number;
  subtotal: number; discountType: string; setDiscountType: (v: string) => void;
  discountValue: string; setDiscountValue: (v: string) => void; discountAmount: number;
  paymentMethod: string; setPaymentMethod: (v: string) => void;
  amountReceived: string; setAmountReceived: (v: string) => void;
  changeAmount: number; clientName: string; setClientName: (v: string) => void;
  clientPhone: string; setClientPhone: (v: string) => void;
  notes: string; setNotes: (v: string) => void;
  updateQuantity: (id: string, delta: number) => void;
  removeFromCart: (id: string) => void;
  updateCartStaff: (id: string, staffId: string) => void;
  finalizeSale: () => void;
  onSwapItem: (itemId: string) => void;
  onSaveAccount: () => void;
  activeAccountId: string | null;
  operatorName: string;
}) => (
  <div className="flex flex-col h-full">
    <div className="p-4 border-b border-border flex items-center justify-between">
      <h2 className="font-display font-semibold flex items-center gap-2"><Receipt size={16} /> Carrinho</h2>
      <div className="flex items-center gap-2">
        {cart.length > 0 && <Badge variant="secondary" className="text-xs">{cart.reduce((s, i) => s + i.quantity, 0)} itens</Badge>}
      </div>
    </div>

    <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-2">
      {cart.length === 0 && <p className="text-center text-muted-foreground text-sm py-8">Clique num serviço ou produto para adicionar</p>}
      {cart.map(item => (
        <div key={item.id} className="rounded-lg border border-border p-2.5 sm:p-3 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                {item.type === "service" ? <Scissors size={11} className="text-primary shrink-0" /> : <Package size={11} className="text-accent-foreground shrink-0" />}
                <p className="font-medium text-sm text-foreground truncate">{item.name}</p>
              </div>
              <p className="text-xs text-muted-foreground">€{item.price.toFixed(2)} × {item.quantity}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button size="icon" variant="outline" className="h-6 w-6" onClick={() => updateQuantity(item.id, -1)} disabled={item.quantity <= 1}><Minus size={10} /></Button>
              <span className="text-xs font-medium w-5 text-center">{item.quantity}</span>
              <Button size="icon" variant="outline" className="h-6 w-6" onClick={() => updateQuantity(item.id, 1)}><Plus size={10} /></Button>
              <span className="font-bold text-sm text-primary ml-1 w-12 text-right">€{(item.price * item.quantity).toFixed(2)}</span>
              {item.type === "service" && (
                <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-primary" title="Trocar serviço" onClick={() => onSwapItem(item.id)}>
                  <ArrowLeftRight size={11} />
                </Button>
              )}
              <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => removeFromCart(item.id)}><Trash2 size={11} /></Button>
            </div>
          </div>
          {item.type === "service" && staffList.length > 0 && (
            <Select value={item.staffId} onValueChange={(v) => updateCartStaff(item.id, v)}>
              <SelectTrigger className="h-7 text-xs"><Users size={10} className="mr-1" /><SelectValue placeholder="Colaborador" /></SelectTrigger>
              <SelectContent>{staffList.map(s => (<SelectItem key={s.id} value={s.id} className="text-xs">{s.name}</SelectItem>))}</SelectContent>
            </Select>
          )}
        </div>
      ))}
    </div>

    {/* Payment section */}
    <div className="border-t border-border p-3 sm:p-4 space-y-3 bg-accent/20">
      <div className="grid grid-cols-2 gap-2">
        <Input value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Nome cliente" className="h-8 text-xs" />
        <Input value={clientPhone} onChange={e => setClientPhone(e.target.value)} placeholder="Telefone" className="h-8 text-xs" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
        {[
          { value: "cash", label: "Dinheiro", icon: Banknote },
          { value: "card", label: "Cartão", icon: CreditCard },
          { value: "mbway", label: "MB Way", icon: Smartphone },
          { value: "multibanco", label: "Multibanco", icon: CreditCard },
        ].map(m => (
          <button key={m.value} onClick={() => setPaymentMethod(m.value)}
            className={`flex flex-col items-center gap-0.5 rounded-lg border p-2 text-[10px] font-medium transition-all ${paymentMethod === m.value ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/30"}`}>
            <m.icon size={14} />{m.label}
          </button>
        ))}
      </div>
      {paymentMethod === "cash" && (
        <div className="grid grid-cols-2 gap-2 items-end">
          <div>
            <Label className="text-[11px]">Valor Recebido (€)</Label>
            <Input type="number" step="0.01" value={amountReceived} onChange={e => setAmountReceived(e.target.value)} placeholder="0.00" className="h-9" />
          </div>
          <div className="rounded-lg bg-primary/10 p-2 text-center">
            <p className="text-[10px] text-muted-foreground">Troco</p>
            <p className="font-bold text-primary text-lg">€{changeAmount.toFixed(2)}</p>
          </div>
        </div>
      )}
      <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notas (opcional)" rows={1} className="text-xs" />

      {/* Desconto */}
      <div className="rounded-lg border border-border p-2.5 space-y-2 bg-background/60">
        <Label className="text-[11px] flex items-center gap-1"><Percent size={11} className="text-primary" /> Desconto</Label>
        <div className="grid grid-cols-2 gap-2">
          <Select value={discountType} onValueChange={setDiscountType}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none" className="text-xs">Sem desconto</SelectItem>
              <SelectItem value="percent" className="text-xs">Percentagem (%)</SelectItem>
              <SelectItem value="fixed" className="text-xs">Valor fixo (€)</SelectItem>
            </SelectContent>
          </Select>
          <Input type="number" step="0.01" min="0" className="h-8 text-xs" disabled={discountType === "none"}
            value={discountValue} onChange={e => setDiscountValue(e.target.value)}
            placeholder={discountType === "percent" ? "% desconto" : "€ desconto"} />
        </div>
        {discountAmount > 0 && (
          <div className="text-[11px] space-y-0.5">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>€{subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between text-destructive"><span>Desconto</span><span>-€{discountAmount.toFixed(2)}</span></div>
            <p className="text-muted-foreground">Por: {operatorName || "operador"}</p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-border">
        <div>
          <p className="text-[11px] text-muted-foreground">Total</p>
          {discountAmount > 0 && <p className="text-[11px] text-muted-foreground line-through">€{subtotal.toFixed(2)}</p>}
          <p className="text-2xl font-bold text-foreground">€{total.toFixed(2)}</p>
        </div>
        <div className="flex gap-2">
          {cart.length > 0 && (
            <Button onClick={onSaveAccount} variant="outline" className="rounded-full px-4 gap-1 text-xs">
              <FolderOpen size={14} /> {activeAccountId ? "Atualizar" : "Guardar"}
            </Button>
          )}
          <Button onClick={finalizeSale} disabled={cart.length === 0} className="rounded-full px-5 gap-2">
            <CheckCircle size={16} /> Fechar Conta
          </Button>
        </div>
      </div>
    </div>
  </div>
);

// ------ Main PDV Component ------
const PDV = () => {
  const { toast } = useToast();
  const receiptRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // Auth state
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userName, setUserName] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [loginError, setLoginError] = useState("");

  // Session management
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [currentSessionToken, setCurrentSessionToken] = useState<string | null>(null);
  const [maxPdvSessions, setMaxPdvSessions] = useState(3);
  const [activeSessions, setActiveSessions] = useState<PdvSession[]>([]);
  const [showActiveSessionsDialog, setShowActiveSessionsDialog] = useState(false);

  // Data
  const [services, setServices] = useState<Service[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [staffList, setStaffList] = useState<{ id: string; name: string }[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [amountReceived, setAmountReceived] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [discountType, setDiscountType] = useState("none");
  const [discountValue, setDiscountValue] = useState("");
  const [completed, setCompleted] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [catalogTab, setCatalogTab] = useState("services");
  const [cartSheetOpen, setCartSheetOpen] = useState(false);

  // Open accounts (mesas)
  const [openAccounts, setOpenAccounts] = useState<OpenAccount[]>([]);
  const [activeAccountId, setActiveAccountId] = useState<string | null>(null);
  const [showNewAccountDialog, setShowNewAccountDialog] = useState(false);
  const [newAccName, setNewAccName] = useState("");
  const [newAccPhone, setNewAccPhone] = useState("");
  const [newAccEmail, setNewAccEmail] = useState("");

  // Swap service dialog
  const [swapItemId, setSwapItemId] = useState<string | null>(null);
  const [swapSearchTerm, setSwapSearchTerm] = useState("");

  // Schedule for later dialog
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({ service_id: "", date: "", time: "", staff_id: "", notes: "" });

  // History & Logs
  const [salesHistory, setSalesHistory] = useState<SaleRecord[]>([]);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [receiptData, setReceiptData] = useState<{ items: CartItem[]; total: number; subtotal?: number; discount?: number; payment: string; client: string; date: string; change: number } | null>(null);

  // Business info
  const [businessName, setBusinessName] = useState("Mente Sã");
  const [businessPhone, setBusinessPhone] = useState("");
  const [businessAddress, setBusinessAddress] = useState("");

  // Cash register
  const [registerOpen, setRegisterOpen] = useState(false);
  const [openingAmount, setOpeningAmount] = useState("");
  const [showClosingScreen, setShowClosingScreen] = useState(false);
  const [closingStep, setClosingStep] = useState<"counting" | "result">("counting");

  // Closing counting
  const [countNotes, setCountNotes] = useState({ n200: 0, n100: 0, n50: 0, n20: 0, n10: 0, n5: 0 });
  const [countCoins, setCountCoins] = useState({ c200: 0, c100: 0, c50: 0, c20: 0, c10: 0, c5: 0, c2: 0, c1: 0 });
  const [countCard, setCountCard] = useState("");
  const [countMbway, setCountMbway] = useState("");
  const [countMultibanco, setCountMultibanco] = useState("");

  // ------ Audit Log Helper ------
  const logAction = useCallback(async (action: string, details: any = {}, relatedId?: string) => {
    if (!currentUser) return;
    await supabase.from("pdv_logs").insert({
      user_id: currentUser.id, user_email: currentUser.email || "",
      user_name: userName, action, details, related_id: relatedId || null,
    });
  }, [currentUser, userName]);

  // ------ Check for existing sessions ------
  const checkUserSession = async (user: User, userIsAdmin: boolean): Promise<{ allowed: boolean; reason?: string }> => {
    // Check if user already has an open session
    const { data: userSessions } = await supabase
      .from("pdv_sessions")
      .select("*")
      .eq("user_id", user.id)
      .eq("status", "open");

    if (userSessions && userSessions.length > 0) {
      if (userIsAdmin) {
        // Admin: auto-close prior sessions and continue
        await supabase.from("pdv_sessions").update({
          status: "closed",
          closed_at: new Date().toISOString(),
          closed_by_user_id: user.id,
          closed_by_name: "Auto (nova entrada admin)",
        }).eq("user_id", user.id).eq("status", "open");
      } else {
        return { allowed: false, reason: "Este utilizador já está conectado noutro PDV. Desconecte a sessão anterior para continuar." };
      }
    }

    // Check max simultaneous sessions
    const { data: maxSetting } = await supabase
      .from("app_settings")
      .select("value")
      .eq("key", "max_pdv_sessions")
      .maybeSingle();

    const max = maxSetting?.value ? Number(maxSetting.value) : 3;
    setMaxPdvSessions(max);

    const { data: allOpenSessions } = await supabase
      .from("pdv_sessions")
      .select("*")
      .eq("status", "open");

    if (allOpenSessions && allOpenSessions.length >= max && !userIsAdmin) {
      return { allowed: false, reason: `Limite máximo de ${max} caixa(s) simultâneo(s) atingido. Contacte o administrador.` };
    }

    return { allowed: true };
  };

  // ------ On Login ------
  const handleLogin = async (user: User) => {
    setCurrentUser(user);
    const { data: profile } = await supabase.from("profiles").select("full_name").eq("user_id", user.id).maybeSingle();
    const name = profile?.full_name || user.email || "Operador";
    setUserName(name);
    const { data: adminRole } = await supabase.from("user_roles").select("role").eq("user_id", user.id).eq("role", "admin").maybeSingle();
    const userIsAdmin = !!adminRole;
    setIsAdmin(userIsAdmin);

    // Check session restrictions
    const check = await checkUserSession(user, userIsAdmin);
    if (!check.allowed) {
      setLoginError(check.reason || "Acesso negado");
      // Don't sign out the global auth session - just block PDV entry
      setCurrentUser(null);
      return;
    }

    setAuthenticated(true);
    setLoginError("");
    await supabase.from("pdv_logs").insert({
      user_id: user.id, user_email: user.email || "", user_name: name,
      action: "LOGIN", details: { timestamp: new Date().toISOString() },
    });
  };


  const handleLogout = async () => {
    await logAction("LOGOUT");
    // Close the PDV session if open
    if (currentSessionId) {
      await supabase.from("pdv_sessions").update({
        status: "closed",
        closed_at: new Date().toISOString(),
        closed_by_user_id: currentUser?.id,
        closed_by_name: userName,
      }).eq("id", currentSessionId);
    }
    await supabase.auth.signOut();
    setCurrentUser(null); setAuthenticated(false); setIsAdmin(false); setUserName(""); setCart([]);
    setOpenAccounts([]); setActiveAccountId(null); setRegisterOpen(false); setCurrentSessionId(null); setCurrentSessionToken(null);
  };

  // ------ Load data ------
  useEffect(() => {
    if (!authenticated) return;
    const loadData = async () => {
      const [sRes, pRes, stRes, bName, bPhone, bAddr, maxS] = await Promise.all([
        supabase.from("services").select("id, name, price, duration_minutes").eq("is_active", true).order("name"),
        supabase.from("products").select("id, name, price, stock, is_promotional, promotional_price").eq("is_active", true).order("name"),
        supabase.from("staff").select("id, name").eq("is_active", true).order("name"),
        supabase.from("app_settings").select("value").eq("key", "business_name").maybeSingle(),
        supabase.from("app_settings").select("value").eq("key", "business_phone").maybeSingle(),
        supabase.from("app_settings").select("value").eq("key", "business_address").maybeSingle(),
        supabase.from("app_settings").select("value").eq("key", "max_pdv_sessions").maybeSingle(),
      ]);
      setServices((sRes.data as Service[]) || []);
      setProducts((pRes.data as Product[]) || []);
      setStaffList((stRes.data as { id: string; name: string }[]) || []);
      if (bName.data?.value) setBusinessName(String(bName.data.value));
      if (bPhone.data?.value) setBusinessPhone(String(bPhone.data.value));
      if (bAddr.data?.value) setBusinessAddress(String(bAddr.data.value));
      if (maxS.data?.value) setMaxPdvSessions(Number(maxS.data.value) || 3);
    };
    loadData(); loadSalesHistory(); loadLogs();
    if (isAdmin) loadActiveSessions();
  }, [authenticated]);

  // Periodic session validity check (every 30s)
  useEffect(() => {
    if (!authenticated || !currentSessionId) return;
    const interval = setInterval(async () => {
      const { data } = await supabase
        .from("pdv_sessions")
        .select("status, session_token")
        .eq("id", currentSessionId)
        .maybeSingle();

      if (data && (data.status === "closed" || data.session_token !== currentSessionToken)) {
        toast({ title: "A sua sessão PDV foi encerrada pelo administrador.", variant: "destructive" });
        setRegisterOpen(false);
        setCurrentSessionId(null);
        setCurrentSessionToken(null);
        handleLogout();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [authenticated, currentSessionId, currentSessionToken]);

  const loadActiveSessions = async () => {
    const { data } = await supabase
      .from("pdv_sessions")
      .select("*")
      .eq("status", "open")
      .order("opened_at", { ascending: false });
    setActiveSessions((data as PdvSession[]) || []);
  };

  const loadSalesHistory = async () => {
    const today = new Date().toISOString().split("T")[0];
    const { data } = await supabase
      .from("bookings")
      .select("id, client_name, total_price, status, booking_date, booking_time, notes, services:service_id(name), staff:staff_id(name)")
      .eq("booking_date", today).eq("status", "completed").order("booking_time", { ascending: false });
    setSalesHistory((data as unknown as SaleRecord[]) || []);
  };

  const loadLogs = async () => {
    const today = new Date().toISOString().split("T")[0];
    const { data } = await supabase
      .from("pdv_logs").select("id, user_email, user_name, action, details, created_at")
      .gte("created_at", `${today}T00:00:00`).order("created_at", { ascending: false }).limit(100);
    setLogs((data as LogEntry[]) || []);
  };

  // ------ Cart Logic ------
  const subtotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.quantity, 0), [cart]);
  const discountAmount = useMemo(() => {
    if (discountType === "none") return 0;
    const v = parseFloat(discountValue) || 0;
    const amount = discountType === "percent" ? (subtotal * v) / 100 : v;
    return Math.min(Math.max(amount, 0), subtotal);
  }, [discountType, discountValue, subtotal]);
  const total = useMemo(() => Math.max(0, subtotal - discountAmount), [subtotal, discountAmount]);
  const changeAmount = useMemo(() => {
    const received = parseFloat(amountReceived) || 0;
    return paymentMethod === "cash" ? Math.max(0, received - total) : 0;
  }, [amountReceived, total, paymentMethod]);

  const filteredServices = useMemo(() => services.filter(s => s.name.toLowerCase().includes(searchTerm.toLowerCase())), [services, searchTerm]);
  const filteredProducts = useMemo(() => products.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase())), [products, searchTerm]);

  const addServiceToCart = (service: Service) => {
    const existing = cart.find(i => i.originalId === service.id && i.type === "service");
    if (existing) {
      setCart(cart.map(i => i.id === existing.id ? { ...i, quantity: i.quantity + 1 } : i));
    } else {
      setCart([...cart, { id: `s-${service.id}-${Date.now()}`, name: service.name, price: service.price, quantity: 1, staffId: staffList[0]?.id || "", type: "service", originalId: service.id }]);
    }
  };

  const addProductToCart = (product: Product) => {
    const effectivePrice = product.is_promotional && product.promotional_price > 0 ? product.promotional_price : product.price;
    const existing = cart.find(i => i.originalId === product.id && i.type === "product");
    if (existing) {
      if (existing.quantity >= product.stock) { toast({ title: "Stock insuficiente", variant: "destructive" }); return; }
      setCart(cart.map(i => i.id === existing.id ? { ...i, quantity: i.quantity + 1 } : i));
    } else {
      if (product.stock <= 0) { toast({ title: "Produto sem stock", variant: "destructive" }); return; }
      setCart([...cart, { id: `p-${product.id}-${Date.now()}`, name: product.name, price: effectivePrice, quantity: 1, staffId: "", type: "product", originalId: product.id }]);
    }
  };

  const updateQuantity = (id: string, delta: number) => {
    setCart(cart.map(i => {
      if (i.id !== id) return i;
      const newQty = i.quantity + delta;
      if (newQty <= 0) return i;
      if (i.type === "product") {
        const product = products.find(p => p.id === i.originalId);
        if (product && newQty > product.stock) { toast({ title: "Stock insuficiente", variant: "destructive" }); return i; }
      }
      return { ...i, quantity: newQty };
    }));
  };

  const removeFromCart = async (id: string) => {
    const item = cart.find(i => i.id === id);
    if (item) {
      await logAction("REMOCAO_ITEM", { item_name: item.name, item_price: item.price, item_type: item.type, account: activeAccountId || "venda_direta" });
    }
    setCart(cart.filter(i => i.id !== id));
  };

  const updateCartStaff = (id: string, staffId: string) => setCart(cart.map(i => i.id === id ? { ...i, staffId } : i));

  // ------ Swap service logic ------
  const handleSwapItem = (itemId: string) => {
    setSwapItemId(itemId);
    setSwapSearchTerm("");
  };

  const confirmSwap = async (newService: Service) => {
    if (!swapItemId) return;
    const oldItem = cart.find(i => i.id === swapItemId);
    if (!oldItem) return;

    await logAction("TROCA_SERVICO", {
      old_service: oldItem.name, old_price: oldItem.price,
      new_service: newService.name, new_price: newService.price,
      account: activeAccountId || "venda_direta",
      client: clientName,
    });

    setCart(cart.map(i => i.id === swapItemId ? {
      ...i, name: newService.name, price: newService.price,
      originalId: newService.id, id: `s-${newService.id}-${Date.now()}`
    } : i));

    setSwapItemId(null);
    toast({ title: `Serviço trocado para "${newService.name}"` });
  };

  // ------ Open Accounts (Mesas) ------
  const createNewAccount = async () => {
    if (!newAccName.trim()) { toast({ title: "Insira o nome do cliente", variant: "destructive" }); return; }
    const acc: OpenAccount = {
      id: `acc-${Date.now()}`,
      clientName: newAccName.trim(),
      clientPhone: newAccPhone.trim(),
      clientEmail: newAccEmail.trim(),
      items: [],
      notes: "",
      openedAt: new Date().toISOString(),
    };
    setOpenAccounts(prev => [...prev, acc]);
    await logAction("ABERTURA_CONTA", { client: acc.clientName, phone: acc.clientPhone });
    toast({ title: `Conta aberta para "${acc.clientName}"` });
    setShowNewAccountDialog(false);
    setNewAccName(""); setNewAccPhone(""); setNewAccEmail("");
    loadAccount(acc);
  };

  const loadAccount = (acc: OpenAccount) => {
    if (activeAccountId) {
      setOpenAccounts(prev => prev.map(a => a.id === activeAccountId ? { ...a, items: cart, notes, clientName, clientPhone } : a));
    }
    setActiveAccountId(acc.id);
    setCart(acc.items);
    setClientName(acc.clientName);
    setClientPhone(acc.clientPhone);
    setNotes(acc.notes);
    setCatalogTab("services");
  };

  const saveCurrentAccount = async () => {
    if (!activeAccountId) {
      if (!clientName.trim()) { toast({ title: "Insira o nome do cliente para guardar", variant: "destructive" }); return; }
      const acc: OpenAccount = {
        id: `acc-${Date.now()}`, clientName: clientName.trim(), clientPhone, clientEmail: "",
        items: [...cart], notes, openedAt: new Date().toISOString(),
      };
      setOpenAccounts(prev => [...prev, acc]);
      setActiveAccountId(acc.id);
      await logAction("ABERTURA_CONTA", { client: acc.clientName, items: cart.map(i => ({ name: i.name, qty: i.quantity })) });
      toast({ title: `Conta guardada para "${acc.clientName}"` });
    } else {
      setOpenAccounts(prev => prev.map(a => a.id === activeAccountId ? { ...a, items: [...cart], notes, clientName, clientPhone } : a));
      await logAction("ATUALIZACAO_CONTA", { account_id: activeAccountId, client: clientName, items: cart.map(i => ({ name: i.name, qty: i.quantity })) });
      toast({ title: "Conta atualizada" });
    }
  };

  const closeAccount = (accId: string) => {
    if (activeAccountId === accId) {
      setActiveAccountId(null);
      setCart([]); setClientName(""); setClientPhone(""); setNotes("");
    }
    setOpenAccounts(prev => prev.filter(a => a.id !== accId));
  };

  const startNewDirectSale = () => {
    if (activeAccountId) {
      setOpenAccounts(prev => prev.map(a => a.id === activeAccountId ? { ...a, items: cart, notes, clientName, clientPhone } : a));
    }
    setActiveAccountId(null);
    setCart([]); setClientName(""); setClientPhone(""); setNotes("");
  };

  // ------ Finalize Sale ------
  const finalizeSale = async () => {
    if (cart.length === 0) { toast({ title: "Adicione pelo menos um item", variant: "destructive" }); return; }
    if (paymentMethod === "cash" && (parseFloat(amountReceived) || 0) < total) { toast({ title: "Valor recebido insuficiente", variant: "destructive" }); return; }

    const today = new Date().toISOString().split("T")[0];
    const now = new Date().toTimeString().substring(0, 5);
    const payLabel = paymentMethod === "cash" ? "Dinheiro" : paymentMethod === "card" ? "Cartão" : paymentMethod === "mbway" ? "MB Way" : paymentMethod === "multibanco" ? "Multibanco" : paymentMethod;
    const createdIds: string[] = [];
    const discountRatio = subtotal > 0 ? discountAmount / subtotal : 0;
    const discountAuthor = discountAmount > 0 ? (userName || currentUser?.email || "PDV") : null;

    for (const item of cart.filter(i => i.type === "service")) {
      const gross = item.price * item.quantity;
      const itemDiscount = Number((gross * discountRatio).toFixed(2));
      const { data } = await supabase.from("bookings").insert({
        service_id: item.originalId, client_name: clientName || "Cliente PDV", client_phone: clientPhone, client_email: "",
        booking_date: today, booking_time: now, location_type: "studio", address: businessAddress || "Espaço",
        total_price: Number((gross - itemDiscount).toFixed(2)), delivery_fee: 0, status: "completed",
        original_price: gross,
        discount_type: discountAmount > 0 ? discountType : "none",
        discount_value: discountAmount > 0 ? parseFloat(discountValue) || 0 : 0,
        discount_amount: itemDiscount,
        discount_by: discountAuthor,
        discount_at: discountAmount > 0 ? new Date().toISOString() : null,
        staff_id: item.staffId || null, notes: `PDV | ${payLabel} | Op: ${userName} | ${notes}`.trim(),
      }).select("id").single();
      if (data) createdIds.push(data.id);
    }

    const productItems = cart.filter(i => i.type === "product");
    if (productItems.length > 0) {
      const orderGross = productItems.reduce((s, i) => s + i.price * i.quantity, 0);
      const orderDiscount = Number((orderGross * discountRatio).toFixed(2));
      const { data: order } = await supabase.from("orders").insert({
        client_name: clientName || "Cliente PDV", client_phone: clientPhone, client_email: "",
        delivery_method: "pickup", total_amount: Number((orderGross - orderDiscount).toFixed(2)), status: "delivered",
        discount_amount: orderDiscount, discount_by: discountAuthor,
        notes: `PDV | ${payLabel} | Op: ${userName} | ${notes}`.trim(),
      }).select("id").single();
      if (order) {
        createdIds.push(order.id);
        for (const item of productItems) {
          await supabase.from("order_items").insert({ order_id: order.id, product_id: item.originalId, product_name: item.name, quantity: item.quantity, unit_price: item.price });
          const product = products.find(p => p.id === item.originalId);
          if (product) await supabase.from("products").update({ stock: Math.max(0, product.stock - item.quantity) }).eq("id", item.originalId);
        }
      }
    }

    await logAction("VENDA", {
      items: cart.map(i => ({ name: i.name, qty: i.quantity, price: i.price, type: i.type })),
      total, subtotal, discount_amount: discountAmount, discount_type: discountType,
      discount_value: parseFloat(discountValue) || 0, discount_by: discountAuthor,
      payment: payLabel, client: clientName || "Cliente PDV", change: changeAmount,
      from_account: activeAccountId || null,
    }, createdIds.join(","));

    if (activeAccountId) {
      closeAccount(activeAccountId);
    }

    setReceiptData({ items: [...cart], total, subtotal, discount: discountAmount, payment: payLabel, client: clientName || "Cliente PDV", date: `${today} ${now}`, change: changeAmount });
    setCompleted(true); setCartSheetOpen(false);
    toast({ title: "Venda registada com sucesso!" });
    loadSalesHistory(); loadLogs();

    const { data: rp } = await supabase.from("products").select("id, name, price, stock, is_promotional, promotional_price").eq("is_active", true).order("name");
    if (rp) setProducts(rp as Product[]);
  };

  const cancelSale = async (saleId: string) => {
    if (!confirm("Cancelar esta venda?")) return;
    await supabase.from("bookings").update({ status: "cancelled" }).eq("id", saleId);
    await logAction("CANCELAMENTO", { booking_id: saleId }, saleId);
    toast({ title: "Venda cancelada e registada no log" });
    loadSalesHistory(); loadLogs();
  };

  const deleteLog = async (logId: string) => {
    if (!confirm("Eliminar este registo de log?")) return;
    await supabase.from("pdv_logs").delete().eq("id", logId);
    toast({ title: "Log eliminado" }); loadLogs();
  };

  const printReceipt = () => {
    if (!receiptRef.current) return;
    const pw = window.open("", "_blank", "width=320,height=600");
    if (!pw) return;
    pw.document.write(`<html><head><title>Recibo</title><style>body{font-family:'Courier New',monospace;font-size:12px;width:280px;margin:0 auto;padding:10px;}.center{text-align:center;}.bold{font-weight:bold;}.line{border-top:1px dashed #000;margin:8px 0;}.row{display:flex;justify-content:space-between;}h2{margin:4px 0;font-size:16px;}p{margin:2px 0;}</style></head><body>${receiptRef.current.innerHTML}<script>window.print();window.close();</script></body></html>`);
    pw.document.close();
  };

  const resetSale = () => {
    setCart([]); setPaymentMethod("cash"); setAmountReceived(""); setClientName(""); setClientPhone(""); setNotes(""); setDiscountType("none"); setDiscountValue(""); setCompleted(false); setReceiptData(null); setActiveAccountId(null);
  };

  // ------ Cash Register Open ------
  const handleOpenRegister = async () => {
    const val = parseFloat(openingAmount) || 0;

    // Create pdv_session record
    const { data: session } = await supabase.from("pdv_sessions").insert({
      user_id: currentUser!.id,
      user_email: currentUser!.email || "",
      user_name: userName,
      status: "open",
      opening_amount: val,
    }).select("id, session_token").single();

    if (session) {
      setCurrentSessionId(session.id);
      setCurrentSessionToken(session.session_token);
    }

    setRegisterOpen(true);
    await logAction("ABERTURA_CAIXA", { opening_amount: val, session_id: session?.id, timestamp: new Date().toISOString() });
    toast({ title: `Caixa aberto com €${val.toFixed(2)}, ${format(new Date(), "HH:mm 'de' dd/MM/yyyy", { locale: pt })}` });
  };

  // ------ Cash Register Close Calculations ------
  const cashCounted = useMemo(() => {
    return (
      countNotes.n200 * 200 + countNotes.n100 * 100 + countNotes.n50 * 50 +
      countNotes.n20 * 20 + countNotes.n10 * 10 + countNotes.n5 * 5 +
      countCoins.c200 * 2 + countCoins.c100 * 1 + countCoins.c50 * 0.5 +
      countCoins.c20 * 0.2 + countCoins.c10 * 0.1 + countCoins.c5 * 0.05 +
      countCoins.c2 * 0.02 + countCoins.c1 * 0.01
    );
  }, [countNotes, countCoins]);

  const cardCounted = parseFloat(countCard) || 0;
  const mbwayCounted = parseFloat(countMbway) || 0;
  const multibancoCounted = parseFloat(countMultibanco) || 0;
  const totalCounted = cashCounted + cardCounted + mbwayCounted + multibancoCounted;

  const openVal = parseFloat(openingAmount) || 0;
  const expectedCash = openVal + salesHistory.reduce((s, r) => {
    if ((r.notes || "").includes("Dinheiro")) return s + Number(r.total_price);
    return s;
  }, 0);
  const expectedCard = salesHistory.reduce((s, r) => (r.notes || "").includes("Cartão") ? s + Number(r.total_price) : s, 0);
  const expectedMbway = salesHistory.reduce((s, r) => (r.notes || "").includes("MB Way") ? s + Number(r.total_price) : s, 0);
  const expectedMultibanco = salesHistory.reduce((s, r) => (r.notes || "").includes("Multibanco") ? s + Number(r.total_price) : s, 0);
  const expectedTotal = expectedCash + expectedCard + expectedMbway + expectedMultibanco;
  const difference = totalCounted - expectedTotal;
  const registerMatches = Math.abs(difference) < 0.02;

  const handleCloseRegister = async () => {
    // Only admin can close (or the user who opened)
    if (!isAdmin) {
      toast({ title: "Apenas o administrador pode fechar o caixa.", variant: "destructive" });
      return;
    }

    await logAction("FECHO_CAIXA", {
      opening_amount: openVal, cash_counted: cashCounted, card_counted: cardCounted,
      mbway_counted: mbwayCounted, multibanco_counted: multibancoCounted,
      total_counted: totalCounted, expected_total: expectedTotal, difference,
      matches: registerMatches, notes_breakdown: countNotes, coins_breakdown: countCoins,
      session_id: currentSessionId, timestamp: new Date().toISOString(),
    });

    // Close pdv_session
    if (currentSessionId) {
      await supabase.from("pdv_sessions").update({
        status: "closed",
        closed_at: new Date().toISOString(),
        closing_amount: totalCounted,
        closed_by_user_id: currentUser?.id,
        closed_by_name: userName,
        details: { difference, matches: registerMatches, expected: expectedTotal, counted: totalCounted },
      }).eq("id", currentSessionId);
    }

    toast({ title: registerMatches ? "Caixa fechado, valores corretos!" : "Caixa fechado com diferença registada" });
    setRegisterOpen(false); setShowClosingScreen(false); setClosingStep("counting"); setOpeningAmount("");
    setCountNotes({ n200: 0, n100: 0, n50: 0, n20: 0, n10: 0, n5: 0 });
    setCountCoins({ c200: 0, c100: 0, c50: 0, c20: 0, c10: 0, c5: 0, c2: 0, c1: 0 });
    setCountCard(""); setCountMbway(""); setCountMultibanco("");
    setCurrentSessionId(null); setCurrentSessionToken(null);
  };

  // Admin: force close another user's session
  const adminForceCloseSession = async (sessionId: string) => {
    if (!confirm("Fechar esta sessão PDV? O utilizador será desconectado.")) return;
    await supabase.from("pdv_sessions").update({
      status: "closed",
      closed_at: new Date().toISOString(),
      closed_by_user_id: currentUser?.id,
      closed_by_name: userName + " (Admin)",
    }).eq("id", sessionId);
    await logAction("FECHO_CAIXA_ADMIN", { closed_session_id: sessionId });
    toast({ title: "Sessão PDV encerrada pelo administrador." });
    loadActiveSessions();
  };

  const startClosing = () => { setShowClosingScreen(true); setClosingStep("counting"); };

  // ------ Not authenticated ------
  if (!authenticated) return <PDVLogin onLogin={handleLogin} sessionError={loginError} />;

  // ------ Cash Register Opening ------
  if (!registerOpen) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-sm w-full p-6 sm:p-8 space-y-6">
          <div className="text-center">
            <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-3"><DoorOpen size={28} className="text-primary" /></div>
            <h2 className="font-display text-2xl font-bold">Abertura de Caixa</h2>
            <p className="text-sm text-muted-foreground">Operador: {userName}</p>
            {isAdmin && <Badge className="mt-2 text-xs gap-1"><Shield size={10} /> Administrador</Badge>}
          </div>
          <div className="space-y-3">
            <div>
              <Label>Valor inicial do caixa (€)</Label>
              <Input type="number" step="0.01" value={openingAmount} onChange={e => setOpeningAmount(e.target.value)} placeholder="0.00" className="text-center text-lg font-bold h-12" autoFocus />
            </div>
            <Button onClick={handleOpenRegister} className="w-full rounded-full gap-2"><DoorOpen size={16} /> Abrir Caixa</Button>

            {/* Admin: view active sessions */}
            {isAdmin && (
              <Button variant="outline" onClick={() => { loadActiveSessions(); setShowActiveSessionsDialog(true); }} className="w-full rounded-full gap-2 text-xs">
                <Monitor size={14} /> Sessões PDV Ativas
              </Button>
            )}

            <Button variant="ghost" onClick={handleLogout} className="w-full gap-2 text-muted-foreground"><LogOut size={14} /> Sair</Button>
          </div>
        </Card>

        {/* Active Sessions Dialog (Admin) */}
        <Dialog open={showActiveSessionsDialog} onOpenChange={setShowActiveSessionsDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Monitor size={18} /> Sessões PDV Ativas ({activeSessions.length}/{maxPdvSessions})</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-muted-foreground">Máximo de caixas simultâneos: <strong>{maxPdvSessions}</strong>. Altere nas Definições &gt; API.</p>
            {activeSessions.length === 0 ? (
              <p className="text-center text-muted-foreground py-6">Nenhuma sessão ativa.</p>
            ) : (
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {activeSessions.map(s => (
                  <div key={s.id} className="rounded-lg border border-border p-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm text-foreground">{s.user_name || s.user_email}</p>
                      <p className="text-xs text-muted-foreground">
                        Aberto: {format(new Date(s.opened_at), "dd/MM HH:mm", { locale: pt })} · Fundo: €{Number(s.opening_amount).toFixed(2)}
                      </p>
                    </div>
                    <Button size="sm" variant="destructive" className="rounded-full text-xs gap-1" onClick={() => adminForceCloseSession(s.id)}>
                      <DoorClosed size={12} /> Fechar
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ------ Cash Register Closing ------
  if (showClosingScreen) {
    const NoteInput = ({ label, value, field }: { label: string; value: number; field: keyof typeof countNotes }) => (
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium w-14">{label}</span>
        <div className="flex items-center gap-1.5">
          <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setCountNotes(p => ({ ...p, [field]: Math.max(0, p[field] - 1) }))} disabled={value <= 0}><Minus size={12} /></Button>
          <span className="w-8 text-center font-bold text-sm">{value}</span>
          <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setCountNotes(p => ({ ...p, [field]: p[field] + 1 }))}><Plus size={12} /></Button>
        </div>
        <span className="text-xs text-muted-foreground w-14 text-right">€{(value * parseInt(label.replace("€", ""))).toFixed(2)}</span>
      </div>
    );
    const CoinInput = ({ label, value, field, unitVal }: { label: string; value: number; field: keyof typeof countCoins; unitVal: number }) => (
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium w-14">{label}</span>
        <div className="flex items-center gap-1.5">
          <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setCountCoins(p => ({ ...p, [field]: Math.max(0, p[field] - 1) }))} disabled={value <= 0}><Minus size={12} /></Button>
          <span className="w-8 text-center font-bold text-sm">{value}</span>
          <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setCountCoins(p => ({ ...p, [field]: p[field] + 1 }))}><Plus size={12} /></Button>
        </div>
        <span className="text-xs text-muted-foreground w-14 text-right">€{(value * unitVal).toFixed(2)}</span>
      </div>
    );

    if (closingStep === "result") {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="max-w-md w-full p-5 sm:p-6 space-y-5">
            <div className="text-center">
              {registerMatches ? (
                <><CheckCircle size={48} className="text-primary mx-auto mb-3" /><h2 className="font-display text-xl sm:text-2xl font-bold text-primary">Caixa Bateu! </h2><p className="text-sm text-muted-foreground">Os valores conferem.</p></>
              ) : (
                <><XCircle size={48} className="text-destructive mx-auto mb-3" /><h2 className="font-display text-xl sm:text-2xl font-bold text-destructive">Diferença Encontrada</h2><p className="text-sm text-muted-foreground">Existe uma diferença no fecho de caixa.</p></>
              )}
            </div>
            <div className="rounded-lg border border-border p-3 sm:p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span>Esperado:</span><span className="font-bold">€{expectedTotal.toFixed(2)}</span></div>
              <div className="flex justify-between"><span>Contado:</span><span className="font-bold">€{totalCounted.toFixed(2)}</span></div>
              <div className="border-t border-border pt-2 flex justify-between font-bold">
                <span>Diferença:</span>
                <span className={difference >= 0 ? "text-primary" : "text-destructive"}>{difference >= 0 ? "+" : ""}€{difference.toFixed(2)}</span>
              </div>
            </div>
            {!isAdmin && (
              <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3">
                <p className="text-xs text-amber-700 flex items-center gap-2"><Shield size={12} /> Apenas o administrador pode confirmar o fecho de caixa.</p>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Button variant="outline" onClick={() => setClosingStep("counting")} className="flex-1 rounded-full gap-2"><Eye size={14} /> Verificar Novamente</Button>
              <Button onClick={handleCloseRegister} className="flex-1 rounded-full gap-2" variant={registerMatches ? "default" : "destructive"} disabled={!isAdmin}>
                <DoorClosed size={14} /> Fechar Caixa
              </Button>
            </div>
          </Card>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-lg w-full p-4 sm:p-6 space-y-4 max-h-[95vh] overflow-y-auto">
          <div className="text-center">
            <DoorClosed size={36} className="text-primary mx-auto mb-2" />
            <h2 className="font-display text-xl font-bold">Fecho de Caixa</h2>
            <p className="text-xs text-muted-foreground">Operador: {userName}</p>
          </div>
          <div className="space-y-2">
            <h3 className="font-semibold text-sm flex items-center gap-2"><Banknote size={14} className="text-primary" /> Notas</h3>
            <div className="rounded-lg border border-border p-2 sm:p-3 space-y-2">
              <NoteInput label="€200" value={countNotes.n200} field="n200" /><NoteInput label="€100" value={countNotes.n100} field="n100" />
              <NoteInput label="€50" value={countNotes.n50} field="n50" /><NoteInput label="€20" value={countNotes.n20} field="n20" />
              <NoteInput label="€10" value={countNotes.n10} field="n10" /><NoteInput label="€5" value={countNotes.n5} field="n5" />
            </div>
          </div>
          <div className="space-y-2">
            <h3 className="font-semibold text-sm flex items-center gap-2"><Coins size={14} className="text-primary" /> Moedas</h3>
            <div className="rounded-lg border border-border p-2 sm:p-3 space-y-2">
              <CoinInput label="€2" value={countCoins.c200} field="c200" unitVal={2} /><CoinInput label="€1" value={countCoins.c100} field="c100" unitVal={1} />
              <CoinInput label="€0,50" value={countCoins.c50} field="c50" unitVal={0.5} /><CoinInput label="€0,20" value={countCoins.c20} field="c20" unitVal={0.2} />
              <CoinInput label="€0,10" value={countCoins.c10} field="c10" unitVal={0.1} /><CoinInput label="€0,05" value={countCoins.c5} field="c5" unitVal={0.05} />
              <CoinInput label="€0,02" value={countCoins.c2} field="c2" unitVal={0.02} /><CoinInput label="€0,01" value={countCoins.c1} field="c1" unitVal={0.01} />
            </div>
            <div className="flex justify-end"><Badge variant="secondary" className="text-xs">Total dinheiro: €{cashCounted.toFixed(2)}</Badge></div>
          </div>
          <div className="space-y-2">
            <h3 className="font-semibold text-sm flex items-center gap-2"><CreditCard size={14} className="text-primary" /> Outros Métodos</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
              <div><Label className="text-[11px]">Cartão (€)</Label><Input type="number" step="0.01" value={countCard} onChange={e => setCountCard(e.target.value)} placeholder="0.00" className="h-8 text-xs" /></div>
              <div><Label className="text-[11px]">MB Way (€)</Label><Input type="number" step="0.01" value={countMbway} onChange={e => setCountMbway(e.target.value)} placeholder="0.00" className="h-8 text-xs" /></div>
              <div><Label className="text-[11px]">Multibanco (€)</Label><Input type="number" step="0.01" value={countMultibanco} onChange={e => setCountMultibanco(e.target.value)} placeholder="0.00" className="h-8 text-xs" /></div>
            </div>
          </div>
          <div className="rounded-lg bg-primary/10 p-3 text-center">
            <p className="text-[11px] text-muted-foreground">Total Contado</p>
            <p className="text-2xl font-bold text-primary">€{totalCounted.toFixed(2)}</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowClosingScreen(false)} className="flex-1 rounded-full">Cancelar</Button>
            <Button onClick={() => setClosingStep("result")} className="flex-1 rounded-full gap-2"><CheckCircle size={14} /> Verificar</Button>
          </div>
        </Card>
      </div>
    );
  }

  // ------ Completed > receipt ------
  if (completed && receiptData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-5 sm:p-6 space-y-4">
          <div className="text-center">
            <CheckCircle size={48} className="text-primary mx-auto mb-3" />
            <h2 className="font-display text-2xl font-bold">Venda Concluída!</h2>
            <p className="text-xs text-muted-foreground">Operador: {userName}</p>
          </div>
          <div ref={receiptRef} className="border border-border rounded-lg p-3 sm:p-4 bg-card text-xs space-y-2">
            <div className="text-center">
              <h2 className="font-bold text-base">{businessName}</h2>
              {businessAddress && <p className="text-muted-foreground">{businessAddress}</p>}
              {businessPhone && <p className="text-muted-foreground">Tel: {businessPhone}</p>}
            </div>
            <div className="border-t border-dashed border-border my-2" />
            <p className="text-muted-foreground">{receiptData.date}</p>
            <p>Cliente: {receiptData.client}</p>
            <p>Operador: {userName}</p>
            <div className="border-t border-dashed border-border my-2" />
            {receiptData.items.map((item, i) => (
              <div key={i} className="flex justify-between">
                <span>{item.quantity}x {item.name}</span>
                <span className="font-medium">€{(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
            <div className="border-t border-dashed border-border my-2" />
            {(receiptData.discount || 0) > 0 && (
              <>
                <div className="flex justify-between"><span>Subtotal</span><span>€{(receiptData.subtotal || 0).toFixed(2)}</span></div>
                <div className="flex justify-between"><span>Desconto</span><span>-€{(receiptData.discount || 0).toFixed(2)}</span></div>
              </>
            )}
            <div className="flex justify-between font-bold text-sm"><span>TOTAL</span><span>€{receiptData.total.toFixed(2)}</span></div>
            <p>Pagamento: {receiptData.payment}</p>
            {receiptData.change > 0 && <p className="font-bold">Troco: €{receiptData.change.toFixed(2)}</p>}
            <div className="border-t border-dashed border-border my-2" />
            <p className="text-center text-muted-foreground">Obrigado pela preferência!</p>
          </div>
          <div className="flex gap-3 justify-center">
            <Button onClick={printReceipt} variant="outline" className="rounded-full gap-2"><Printer size={14} /> Imprimir</Button>
            <Button onClick={resetSale} className="rounded-full gap-2"><Plus size={14} /> Nova Venda</Button>
          </div>
        </Card>
      </div>
    );
  }

  const todayTotal = salesHistory.reduce((s, r) => s + Number(r.total_price), 0);
  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);

  const actionLabels: Record<string, string> = {
    LOGIN: "Login", LOGOUT: "Logout", VENDA: "Venda", CANCELAMENTO: "Cancelamento",
    ABERTURA_CAIXA: "Abertura Caixa", FECHO_CAIXA: "Fecho Caixa", FECHO_CAIXA_ADMIN: "Fecho Admin",
    TROCA_SERVICO: "Troca Serviço", REMOCAO_ITEM: "Remoção Item",
    ABERTURA_CONTA: "Abertura Conta", ATUALIZACAO_CONTA: "Atualização Conta",
    AGENDAMENTO_PDV: "Agendamento PDV",
  };

  const swapServices = services.filter(s => s.name.toLowerCase().includes(swapSearchTerm.toLowerCase()));

  const handleScheduleBooking = async () => {
    if (!scheduleForm.service_id || !scheduleForm.date || !scheduleForm.time) {
      toast({ title: "Preencha serviço, data e hora", variant: "destructive" }); return;
    }
    const svc = services.find(s => s.id === scheduleForm.service_id);
    if (!svc) return;
    const { error } = await supabase.from("bookings").insert({
      service_id: scheduleForm.service_id,
      client_name: clientName || "Cliente PDV",
      client_phone: clientPhone,
      client_email: "",
      booking_date: scheduleForm.date,
      booking_time: scheduleForm.time,
      location_type: "studio",
      address: businessAddress || "Espaço",
      total_price: svc.price,
      delivery_fee: 0,
      status: "confirmed",
      staff_id: scheduleForm.staff_id || null,
      notes: `Agendado via PDV | Op: ${userName} | ${scheduleForm.notes}`.trim(),
    });
    if (error) { toast({ title: "Erro ao agendar", description: error.message, variant: "destructive" }); return; }
    await logAction("AGENDAMENTO_PDV", { service: svc.name, date: scheduleForm.date, time: scheduleForm.time, client: clientName || "Cliente PDV" });
    toast({ title: `Agendamento criado para ${scheduleForm.date} às ${scheduleForm.time}` });
    setScheduleOpen(false);
    setScheduleForm({ service_id: "", date: "", time: "", staff_id: "", notes: "" });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="border-b border-border bg-card px-3 sm:px-6 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"><CreditCard size={18} className="text-primary" /></div>
          <div className="min-w-0">
            <h1 className="font-display text-base sm:text-lg font-bold truncate">Caixa (PDV)</h1>
            <p className="text-[10px] sm:text-[11px] text-muted-foreground truncate">Op: <span className="font-medium text-foreground">{userName}</span>
              {isAdmin && <span className="ml-1 text-primary">· Admin</span>}
              {activeAccountId && <span className="ml-1 text-primary">· Conta: {clientName}</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <div className="hidden lg:flex items-center gap-2 text-xs text-muted-foreground">
            <CalendarDays size={14} />
            {format(new Date(), "EEEE, d 'de' MMMM", { locale: pt })}
          </div>
          <Badge variant="outline" className="text-[10px] sm:text-xs gap-1 hidden sm:flex"><BarChart3 size={12} /> €{todayTotal.toFixed(2)}</Badge>
          <Button variant="outline" size="sm" onClick={() => setScheduleOpen(true)} className="gap-1 text-[10px] sm:text-xs rounded-full h-8 px-2 sm:px-3">
            <CalendarDays size={14} /> <span className="hidden sm:inline">Agendar</span>
          </Button>

          {/* Only show close button - admin restriction enforced in handleCloseRegister */}
          <Button variant="outline" size="sm" onClick={startClosing} className="gap-1 text-[10px] sm:text-xs rounded-full h-8 px-2 sm:px-3">
            <DoorClosed size={14} /> <span className="hidden sm:inline">Fechar Caixa</span>
          </Button>

          {/* Admin: manage sessions */}
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => { loadActiveSessions(); setShowActiveSessionsDialog(true); }} className="gap-1 text-[10px] sm:text-xs rounded-full h-8 px-2 sm:px-3">
              <Monitor size={14} /> <span className="hidden sm:inline">Sessões</span>
            </Button>
          )}

          {isMobile && (
            <Sheet open={cartSheetOpen} onOpenChange={setCartSheetOpen}>
              <SheetTrigger asChild>
                <Button variant="default" size="sm" className="rounded-full gap-1 relative h-8 px-2.5">
                  <ShoppingCart size={14} />
                  {cartCount > 0 && <span className="absolute -top-1.5 -right-1.5 bg-destructive text-destructive-foreground text-[9px] font-bold rounded-full h-4 w-4 flex items-center justify-center">{cartCount}</span>}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:w-96 p-0 flex flex-col">
                <CartPanel cart={cart} staffList={staffList} total={total} subtotal={subtotal}
                  discountType={discountType} setDiscountType={setDiscountType}
                  discountValue={discountValue} setDiscountValue={setDiscountValue} discountAmount={discountAmount}
                  paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod}
                  amountReceived={amountReceived} setAmountReceived={setAmountReceived} changeAmount={changeAmount}
                  clientName={clientName} setClientName={setClientName} clientPhone={clientPhone} setClientPhone={setClientPhone}
                  notes={notes} setNotes={setNotes} updateQuantity={updateQuantity} removeFromCart={removeFromCart}
                  updateCartStaff={updateCartStaff} finalizeSale={finalizeSale} onSwapItem={handleSwapItem}
                  onSaveAccount={saveCurrentAccount} activeAccountId={activeAccountId} operatorName={userName} />
              </SheetContent>
            </Sheet>
          )}
          <Button variant="ghost" size="sm" onClick={handleLogout} className="gap-1 text-[10px] sm:text-xs text-muted-foreground h-8 px-2">
            <LogOut size={14} /> <span className="hidden sm:inline">Sair</span>
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col md:flex-row min-h-0">
        {/* Catalog */}
        <div className="flex-1 p-3 sm:p-6 overflow-y-auto space-y-4">
          {/* Open Accounts bar */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <Button size="sm" variant={!activeAccountId ? "default" : "outline"} className="rounded-full text-xs gap-1 shrink-0 h-8" onClick={startNewDirectSale}>
              <Receipt size={12} /> Venda Direta
            </Button>
            {openAccounts.map(acc => (
              <Button key={acc.id} size="sm" variant={activeAccountId === acc.id ? "default" : "outline"} className="rounded-full text-xs gap-1 shrink-0 h-8" onClick={() => loadAccount(acc)}>
                <FolderOpen size={12} /> {acc.clientName}
                <Badge variant="secondary" className="text-[9px] ml-1 h-4 px-1">{acc.items.length}</Badge>
                {activeAccountId !== acc.id && (
                  <button onClick={(e) => { e.stopPropagation(); closeAccount(acc.id); }} className="ml-0.5 hover:text-destructive"><X size={10} /></button>
                )}
              </Button>
            ))}
            <Button size="sm" variant="ghost" className="rounded-full text-xs gap-1 shrink-0 h-8 text-muted-foreground" onClick={() => setShowNewAccountDialog(true)}>
              <UserPlus size={12} /> Nova Conta
            </Button>
          </div>

          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Pesquisar serviços ou produtos..." className="pl-9 rounded-full" />
          </div>

          <Tabs value={catalogTab} onValueChange={setCatalogTab}>
            <TabsList className="flex-wrap">
              <TabsTrigger value="services" className="gap-1 text-xs sm:text-sm"><Scissors size={13} /> Serviços</TabsTrigger>
              <TabsTrigger value="products" className="gap-1 text-xs sm:text-sm"><ShoppingBag size={13} /> Produtos</TabsTrigger>
              <TabsTrigger value="history" className="gap-1 text-xs sm:text-sm"><Receipt size={13} /> Hoje ({salesHistory.length})</TabsTrigger>
              <TabsTrigger value="logs" className="gap-1 text-xs sm:text-sm"><History size={13} /> Log</TabsTrigger>
            </TabsList>

            <TabsContent value="services" className="mt-4">
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                {filteredServices.map(s => (
                  <button key={s.id} onClick={() => addServiceToCart(s)} className="rounded-xl border border-border bg-card p-3 sm:p-4 text-left hover:border-primary/40 hover:shadow-sm transition-all group">
                    <h3 className="font-semibold text-foreground text-xs sm:text-sm group-hover:text-primary transition-colors leading-tight">{s.name}</h3>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[10px] sm:text-[11px] text-muted-foreground flex items-center gap-1"><Clock size={10} /> {s.duration_minutes}min</span>
                      <span className="font-bold text-primary text-xs sm:text-sm">€{s.price.toFixed(2)}</span>
                    </div>
                  </button>
                ))}
                {filteredServices.length === 0 && <p className="col-span-full text-center text-muted-foreground text-sm py-8">Nenhum serviço encontrado</p>}
              </div>
            </TabsContent>

            <TabsContent value="products" className="mt-4">
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                {filteredProducts.map(p => {
                  const ep = p.is_promotional && p.promotional_price > 0 ? p.promotional_price : p.price;
                  return (
                    <button key={p.id} onClick={() => addProductToCart(p)} disabled={p.stock <= 0} className="rounded-xl border border-border bg-card p-3 sm:p-4 text-left hover:border-primary/40 hover:shadow-sm transition-all group disabled:opacity-50 disabled:cursor-not-allowed">
                      <h3 className="font-semibold text-foreground text-xs sm:text-sm group-hover:text-primary transition-colors leading-tight">{p.name}</h3>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[10px] sm:text-[11px] text-muted-foreground">Stock: {p.stock}</span>
                        <div className="text-right">
                          {p.is_promotional && p.promotional_price > 0 && <span className="text-[10px] text-muted-foreground line-through mr-1">€{p.price.toFixed(2)}</span>}
                          <span className="font-bold text-primary text-xs sm:text-sm">€{ep.toFixed(2)}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
                {filteredProducts.length === 0 && <p className="col-span-full text-center text-muted-foreground text-sm py-8">Nenhum produto encontrado</p>}
              </div>
            </TabsContent>

            <TabsContent value="history" className="mt-4">
              <Card className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Hora</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead className="hidden sm:table-cell">Serviço</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salesHistory.length === 0 ? (
                      <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Sem vendas hoje</TableCell></TableRow>
                    ) : salesHistory.map(s => (
                      <TableRow key={s.id}>
                        <TableCell className="text-xs">{s.booking_time?.substring(0, 5)}</TableCell>
                        <TableCell className="text-xs">{s.client_name}</TableCell>
                        <TableCell className="text-xs hidden sm:table-cell">{(s.services as any)?.name || "-"}</TableCell>
                        <TableCell className="text-right text-xs font-medium">€{Number(s.total_price).toFixed(2)}</TableCell>
                        <TableCell className="text-right"><Button size="sm" variant="ghost" className="text-destructive text-xs h-7" onClick={() => cancelSale(s.id)}>Cancelar</Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
              <div className="flex justify-end mt-3"><Badge className="text-xs">Total: €{todayTotal.toFixed(2)}</Badge></div>
            </TabsContent>

            <TabsContent value="logs" className="mt-4">
              <Card className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Hora</TableHead>
                      <TableHead>Operador</TableHead>
                      <TableHead>Ação</TableHead>
                      <TableHead className="hidden sm:table-cell">Detalhes</TableHead>
                      {isAdmin && <TableHead className="text-right">Ações</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.length === 0 ? (
                      <TableRow><TableCell colSpan={isAdmin ? 5 : 4} className="text-center text-muted-foreground py-8">Sem registos hoje</TableCell></TableRow>
                    ) : logs.map(l => (
                      <TableRow key={l.id}>
                        <TableCell className="text-xs">{new Date(l.created_at).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</TableCell>
                        <TableCell className="text-xs font-medium">{l.user_name || l.user_email}</TableCell>
                        <TableCell>
                          <Badge variant={l.action === "CANCELAMENTO" || l.action === "REMOCAO_ITEM" ? "destructive" : l.action === "TROCA_SERVICO" ? "outline" : "secondary"} className="text-[10px]">
                            {actionLabels[l.action] || l.action}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate hidden sm:table-cell">
                          {l.action === "VENDA" && l.details?.total != null ? `€${Number(l.details.total).toFixed(2)} | ${l.details.payment}` :
                           l.action === "TROCA_SERVICO" ? `${l.details?.old_service} > ${l.details?.new_service}` :
                           l.action === "REMOCAO_ITEM" ? l.details?.item_name :
                           l.action === "ABERTURA_CONTA" || l.action === "ATUALIZACAO_CONTA" ? l.details?.client :
                           l.action === "CANCELAMENTO" ? `ID: ${l.details?.booking_id?.substring(0, 8)}...` :
                           l.action === "FECHO_CAIXA_ADMIN" ? "Encerrado por admin" : "-"}
                        </TableCell>
                        {isAdmin && (
                          <TableCell className="text-right"><Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => deleteLog(l.id)}><Trash2 size={11} /></Button></TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Desktop Cart Sidebar */}
        {!isMobile && (
          <div className="w-[320px] xl:w-[380px] border-l border-border bg-card flex flex-col shrink-0">
            <CartPanel cart={cart} staffList={staffList} total={total} subtotal={subtotal}
              discountType={discountType} setDiscountType={setDiscountType}
              discountValue={discountValue} setDiscountValue={setDiscountValue} discountAmount={discountAmount}
              paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod}
              amountReceived={amountReceived} setAmountReceived={setAmountReceived} changeAmount={changeAmount}
              clientName={clientName} setClientName={setClientName} clientPhone={clientPhone} setClientPhone={setClientPhone}
              notes={notes} setNotes={setNotes} updateQuantity={updateQuantity} removeFromCart={removeFromCart}
              updateCartStaff={updateCartStaff} finalizeSale={finalizeSale} onSwapItem={handleSwapItem}
              onSaveAccount={saveCurrentAccount} activeAccountId={activeAccountId} operatorName={userName} />
          </div>
        )}
      </div>

      {/* New Account Dialog */}
      <Dialog open={showNewAccountDialog} onOpenChange={setShowNewAccountDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus size={18} /> Nova Conta (Mesa)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome do Cliente *</Label><Input value={newAccName} onChange={e => setNewAccName(e.target.value)} placeholder="Nome" autoFocus /></div>
            <div><Label>Telefone</Label><Input value={newAccPhone} onChange={e => setNewAccPhone(e.target.value)} placeholder="Telefone" /></div>
            <div><Label>Email</Label><Input value={newAccEmail} onChange={e => setNewAccEmail(e.target.value)} placeholder="Email" /></div>
            <Button onClick={createNewAccount} className="w-full rounded-full gap-2"><UserPlus size={14} /> Abrir Conta</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Swap Service Dialog */}
      <Dialog open={!!swapItemId} onOpenChange={(open) => !open && setSwapItemId(null)}>
        <DialogContent className="max-w-md max-h-[80vh] flex flex-col">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ArrowLeftRight size={18} /> Trocar Serviço</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Selecione o novo serviço para substituir o atual. A troca será registada no log.</p>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={swapSearchTerm} onChange={e => setSwapSearchTerm(e.target.value)} placeholder="Pesquisar serviço..." className="pl-8 h-8 text-xs" />
          </div>
          <div className="flex-1 overflow-y-auto space-y-1.5">
            {swapServices.map(s => (
              <button key={s.id} onClick={() => confirmSwap(s)} className="w-full flex items-center justify-between rounded-lg border border-border p-3 hover:border-primary/40 hover:bg-primary/5 transition-all text-left">
                <div>
                  <p className="font-medium text-sm text-foreground">{s.name}</p>
                  <p className="text-[11px] text-muted-foreground">{s.duration_minutes}min</p>
                </div>
                <span className="font-bold text-primary text-sm">€{s.price.toFixed(2)}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule Booking Dialog */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CalendarDays size={18} /> Agendar Serviço</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Agende um serviço para outro dia diretamente pelo PDV.</p>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Input value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Nome do cliente" className="h-9 text-sm" />
              <Input value={clientPhone} onChange={e => setClientPhone(e.target.value)} placeholder="Telefone" className="h-9 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Serviço *</Label>
              <Select value={scheduleForm.service_id} onValueChange={v => setScheduleForm({ ...scheduleForm, service_id: v })}>
                <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Selecionar serviço" /></SelectTrigger>
                <SelectContent>{services.map(s => <SelectItem key={s.id} value={s.id} className="text-sm">{s.name}, €{s.price.toFixed(2)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Data *</Label>
                <Input type="date" value={scheduleForm.date} onChange={e => setScheduleForm({ ...scheduleForm, date: e.target.value })} className="h-9 text-sm" min={new Date().toISOString().split("T")[0]} />
              </div>
              <div>
                <Label className="text-xs">Hora *</Label>
                <Input type="time" value={scheduleForm.time} onChange={e => setScheduleForm({ ...scheduleForm, time: e.target.value })} className="h-9 text-sm" />
              </div>
            </div>
            {staffList.length > 0 && (
              <div>
                <Label className="text-xs">Colaborador</Label>
                <Select value={scheduleForm.staff_id} onValueChange={v => setScheduleForm({ ...scheduleForm, staff_id: v })}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Qualquer" /></SelectTrigger>
                  <SelectContent>{staffList.map(s => <SelectItem key={s.id} value={s.id} className="text-sm">{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <Textarea value={scheduleForm.notes} onChange={e => setScheduleForm({ ...scheduleForm, notes: e.target.value })} placeholder="Notas (opcional)" rows={2} className="text-sm" />
            <Button onClick={handleScheduleBooking} className="w-full rounded-full gap-2"><CalendarDays size={14} /> Confirmar Agendamento</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Active Sessions Dialog (Admin - in main view) */}
      <Dialog open={showActiveSessionsDialog} onOpenChange={setShowActiveSessionsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Monitor size={18} /> Sessões PDV Ativas ({activeSessions.length}/{maxPdvSessions})</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">Máximo de caixas simultâneos: <strong>{maxPdvSessions}</strong>. Configure o valor na definição <code>max_pdv_sessions</code> nas Definições.</p>
          {activeSessions.length === 0 ? (
            <p className="text-center text-muted-foreground py-6">Nenhuma sessão ativa.</p>
          ) : (
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {activeSessions.map(s => (
                <div key={s.id} className="rounded-lg border border-border p-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm text-foreground">{s.user_name || s.user_email}</p>
                    <p className="text-xs text-muted-foreground">
                      Aberto: {format(new Date(s.opened_at), "dd/MM HH:mm", { locale: pt })} · Fundo: €{Number(s.opening_amount).toFixed(2)}
                    </p>
                  </div>
                  <Button size="sm" variant="destructive" className="rounded-full text-xs gap-1" onClick={() => adminForceCloseSession(s.id)}>
                    <DoorClosed size={12} /> Fechar
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PDV;
